import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  SnackbarContent,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../../App.css";
import { db, storage } from "../../firebase";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { useChild } from "../../context/ChildContext";
import { sendChildNotification } from "../../utils/notificationHelper";

import useTrackerData from "./useTrackerData";
import LogTab from "./LogTab";
import WeightTab from "./WeightTab";
import EditEntryDialog from "./EditEntryDialog";
import MedicineDialog from "./MedicineDialog";
import { toLocalDatetimeString, CONSISTENCY_OPTIONS } from "./trackerHelpers";

import AwakeTimeChip from "./AwakeTimeChip";
import LeapDialog from "./LeapsDialog";
import SleepTimeline from "../charts/SleepTimeline";
import FeedingTimeline from "../charts/FeedingTimeline";
import DiaryList from "../diary/DiaryList";

// ---------------- Component ----------------

const BabyTracker = () => {
  const { childId } = useParams();
  const { t } = useTranslation();
  const { currentChild, formatChildAge, getLeapStatus, getChildAgeAtEntry } =
    useChild();
  const { previousLeap, daysSinceLastLeap, nextLeap, daysUntilNext, leaps } =
    getLeapStatus(currentChild?.birthDate);

  // Data & subscriptions
  const {
    feedingsData,
    sleepsData,
    log,
    weightLog,
    activeFeeding,
    setActiveFeeding,
    activeSleep,
    setActiveSleep,
    activeTummyTime,
    setActiveTummyTime,
    lastSleep,
    lastFeeding,
  } = useTrackerData(childId);

  // Tab state
  const [tabIndex, setTabIndex] = useState(() => {
    const saved = localStorage.getItem("tabIndex");
    return saved !== null ? parseInt(saved, 10) : 0;
  });
  React.useEffect(() => {
    localStorage.setItem("tabIndex", tabIndex);
  }, [tabIndex]);

  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarBgColor, setSnackbarBgColor] = useState("#fff");
  const showSnackbar = (message, bgColor) => {
    setSnackbarMessage(message);
    setSnackbarBgColor(bgColor ?? "#fff");
    setSnackbarOpen(true);
  };

  // Leap info
  const [showLeapInfo, setShowLeapInfo] = useState(false);
  const [leapDialogOpen, setLeapDialogOpen] = useState(false);

  // Edit dialog state
  const [editingEntry, setEditingEntry] = useState(null);
  const [editingValue, setEditingValue] = useState({});
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingImageFile, setEditingImageFile] = useState(null);
  const [editingImagePreview, setEditingImagePreview] = useState(null);

  // Feeding form state
  const [feedingSide] = useState("none");
  const [extraMilkMM, setExtraMilkMM] = useState("");
  const [extraMilkMME, setExtraMilkMME] = useState("");
  const solidFood = "";
  const [feedingDVitamin] = useState(false);

  // Diaper dialog state
  const [diaperDialogOpen, setDiaperDialogOpen] = useState(false);
  const [diaperForm, setDiaperForm] = useState({
    poop: "poop",
    pee: "pee",
    color: "#FFF59D",
    konsistens: "normal",
  });

  // Medicine dialog state
  const [medicineDialogOpen, setMedicineDialogOpen] = useState(false);
  const [editingMedicine, setEditingMedicine] = useState(null);

  // Sleep form state
  const [sleepHeadPosition] = useState("middle");

  // Extracted milk form state
  const [extractedMilk, setExtractedMilk] = useState("");

  // Firestore collection paths
  const FEEDINGS_COL = `children/${childId}/feedings`;
  const SLEEPS_COL = `children/${childId}/sleeps`;
  const DIAPERS_COL = `children/${childId}/diapers`;
  const EXTRACTED_MILK_COL = `children/${childId}/extractedMilk`;
  const WEIGHTS_COL = `children/${childId}/weights`;
  const FEEDING_PLANS_COL = `children/${childId}/feedingPlans`;
  const ACTIVE_FEEDING_COL = `children/${childId}/activeFeeding`;
  const ACTIVE_SLEEP_COL = `children/${childId}/activeSleep`;
  const ACTIVE_TUMMY_TIME_COL = `children/${childId}/activeTummyTime`;
  const TUMMY_TIMES_COL = `children/${childId}/tummyTimes`;
  const MEDICINES_COL = `children/${childId}/medicines`;

  // ---------------- Image Helpers ----------------

  const compressImage = (file, maxWidth = 1200, quality = 0.7) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement("canvas");
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target.result;
      };
      img.onload = () => {
        const scale = Math.min(maxWidth / img.width, 1);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        canvas
          .getContext("2d")
          .drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            if (blob)
              resolve(
                new File([blob], file.name, {
                  type: "image/jpeg",
                  lastModified: Date.now(),
                }),
              );
            else reject(new Error("Image compression failed"));
          },
          "image/jpeg",
          quality,
        );
      };
      img.onerror = reject;
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // ---------------- Action Handlers ----------------

  const startFeedingHandler = async () => {
    const now = new Date().toISOString();
    const newSession = {
      start: now,
      side: feedingSide,
      created: now,
      extraMilkMM: extraMilkMM ? Number(extraMilkMM) : 0,
      extraMilkMME: extraMilkMME ? Number(extraMilkMME) : 0,
      solidFood: solidFood ? Number(solidFood) : 0,
      solidFoodNote: "",
      dVitamin: feedingDVitamin,
    };
    try {
      const docRef = await addDoc(
        collection(db, ACTIVE_FEEDING_COL),
        newSession,
      );
      setActiveFeeding({ ...newSession, id: docRef.id, active: true });
      setExtraMilkMM("");
      setExtraMilkMME("");
      showSnackbar("Feeding started");
    } catch (error) {
      console.error("Error starting feeding: ", error);
    }
  };

  const stopFeedingHandler = async (e) => {
    e.stopPropagation();
    if (!activeFeeding) return;
    const newEntry = {
      start: activeFeeding.start,
      end: new Date().toISOString(),
      side: activeFeeding.side,
      created: activeFeeding.created,
      extraMilkMM: activeFeeding.extraMilkMM || 0,
      extraMilkMME: activeFeeding.extraMilkMME || 0,
      solidFood: activeFeeding.solidFood || 0,
      solidFoodNote: activeFeeding.solidFoodNote || "",
      dVitamin: activeFeeding.dVitamin || false,
    };
    try {
      await addDoc(collection(db, FEEDINGS_COL), newEntry);
      await deleteDoc(doc(db, ACTIVE_FEEDING_COL, activeFeeding.id));
      setActiveFeeding(null);
      showSnackbar("Feeding stopped");
    } catch (error) {
      console.error("Error stopping feeding: ", error);
    }
  };

  const startSleepHandler = async () => {
    const now = new Date().toISOString();
    const newSession = {
      start: now,
      created: now,
      headPosition: sleepHeadPosition,
    };
    try {
      const docRef = await addDoc(collection(db, ACTIVE_SLEEP_COL), newSession);
      setActiveSleep({ ...newSession, id: docRef.id, active: true });
      showSnackbar("Sleep started");
      sendChildNotification(childId, "sleep_started");
    } catch (error) {
      console.error("Error starting sleep: ", error);
    }
  };

  const stopSleepHandler = async (e) => {
    e.stopPropagation();
    if (!activeSleep) return;
    const newEntry = {
      start: activeSleep.start,
      end: new Date().toISOString(),
      created: activeSleep.created,
      headPosition: activeSleep.headPosition || "middle",
    };
    try {
      await addDoc(collection(db, SLEEPS_COL), newEntry);
      await deleteDoc(doc(db, ACTIVE_SLEEP_COL, activeSleep.id));
      setActiveSleep(null);
      showSnackbar("Sleep stopped");
      sendChildNotification(childId, "sleep_stopped");
    } catch (error) {
      console.error("Error stopping sleep: ", error);
    }
  };

  const startTummyTimeHandler = async () => {
    const now = new Date().toISOString();
    const newSession = { start: now, created: now };
    const docRef = await addDoc(
      collection(db, ACTIVE_TUMMY_TIME_COL),
      newSession,
    );
    setActiveTummyTime({ ...newSession, id: docRef.id, active: true });
    showSnackbar("Tummy time started");
  };

  const stopTummyTimeHandler = async (e) => {
    e.stopPropagation();
    const newEntry = {
      start: activeTummyTime.start,
      end: new Date().toISOString(),
      created: activeTummyTime.created,
    };
    await addDoc(collection(db, TUMMY_TIMES_COL), newEntry);
    await deleteDoc(doc(db, ACTIVE_TUMMY_TIME_COL, activeTummyTime.id));
    setActiveTummyTime(null);
    showSnackbar("Tummy time stopped");
  };

  const startExtractedMilkHandler = async () => {
    const now = new Date().toISOString();
    const newEntry = {
      start: now,
      end: now,
      amount: extractedMilk ? Number(extractedMilk) : 0,
      created: now,
    };
    try {
      await addDoc(collection(db, EXTRACTED_MILK_COL), newEntry);
      setExtractedMilk("");
      showSnackbar("Extracted milk registered");
    } catch (error) {
      console.error("Error registering extracted milk: ", error);
    }
  };

  const openDiaperDialog = async () => {
    const now = new Date().toISOString();
    const defaultEntry = {
      start: now,
      end: now,
      poop: "poop",
      pee: "pee",
      color: "#FFF59D",
      konsistens: "normal",
      created: now,
    };
    try {
      await addDoc(collection(db, DIAPERS_COL), defaultEntry);
      showSnackbar("Diaper entry added");
    } catch (error) {
      console.error("Error adding diaper entry: ", error);
    }
  };

  const saveDiaperEntry = async () => {
    const now = new Date().toISOString();
    const newEntry = {
      start: now,
      end: now,
      poop: diaperForm.poop,
      pee: diaperForm.pee,
      color: diaperForm.poop === "poop" ? diaperForm.color : "",
      konsistens: diaperForm.poop === "poop" ? diaperForm.konsistens : "",
      created: now,
    };
    try {
      await addDoc(collection(db, DIAPERS_COL), newEntry);
      setDiaperDialogOpen(false);
      showSnackbar("Diaper entry added");
    } catch (error) {
      console.error("Error adding diaper entry: ", error);
    }
  };

  const openAddMedicineDialog = () => {
    setEditingMedicine(null);
    setMedicineDialogOpen(true);
  };

  const handleSaveMedicine = async (start, medicines, fever) => {
    try {
      if (editingMedicine) {
        await updateDoc(doc(db, MEDICINES_COL, editingMedicine.id), {
          start,
          medicines,
          fever: fever ?? null,
        });
      } else {
        const now = new Date().toISOString();
        await addDoc(collection(db, MEDICINES_COL), { start, medicines, fever: fever ?? null, created: now });
      }
      setMedicineDialogOpen(false);
      setEditingMedicine(null);
      showSnackbar(editingMedicine ? "Medicin opdateret" : "Medicin registreret");
    } catch (error) {
      console.error("Error saving medicine: ", error);
    }
  };

  const handleDeleteMedicine = async () => {
    if (!editingMedicine) return;
    try {
      await deleteDoc(doc(db, MEDICINES_COL, editingMedicine.id));
      setMedicineDialogOpen(false);
      setEditingMedicine(null);
      showSnackbar("Medicin slettet");
    } catch (error) {
      console.error("Error deleting medicine: ", error);
    }
  };

  const addWeightHandler = async (value) => {
    const now = new Date().toISOString();
    const newEntry = {
      start: now,
      end: now,
      weight: value ? Number(value) : 0,
      created: now,
    };
    try {
      await addDoc(collection(db, WEIGHTS_COL), newEntry);
      showSnackbar("Weight registered");
    } catch (error) {
      console.error("Error registering weight: ", error);
    }
  };

  // ---------------- Edit Dialog ----------------

  const openEditDialog = (entry) => {
    setEditingEntry(entry);
    if (entry.type === "Diaper") {
      setEditingValue({
        datetime: toLocalDatetimeString(entry.start),
        poop: entry.poop,
        pee: entry.pee,
        color: entry.color,
        konsistens: entry.konsistens || "normal",
        imageUrl: entry.imageUrl || null,
      });
      setEditingImageFile(null);
      setEditingImagePreview(entry.imageUrl || null);
    } else if (entry.type === "Feeding") {
      setEditingValue({
        start: toLocalDatetimeString(entry.start),
        end: entry.active ? "" : toLocalDatetimeString(entry.end),
        side: entry.side,
        extraMilkMM: entry.extraMilkMM ? entry.extraMilkMM.toString() : "",
        extraMilkMME: entry.extraMilkMME ? entry.extraMilkMME.toString() : "",
        solidFood: entry.solidFood ? entry.solidFood.toString() : "",
        solidFoodNote: entry.solidFoodNote
          ? entry.solidFoodNote.toString()
          : "",
        dVitamin: entry.dVitamin || false,
        imageUrl: entry.imageUrl || null,
      });
      setEditingImageFile(null);
      setEditingImagePreview(entry.imageUrl || null);
    } else if (entry.type === "Sleep") {
      setEditingValue({
        start: toLocalDatetimeString(entry.start),
        end: entry.active ? "" : toLocalDatetimeString(entry.end),
        headPosition: entry.headPosition || "middle",
      });
    } else if (entry.type === "Tummy Time") {
      setEditingValue({
        start: toLocalDatetimeString(entry.start),
        end: entry.active ? "" : toLocalDatetimeString(entry.end),
      });
    } else if (entry.type === "Extracted Milk") {
      setEditingValue({
        datetime: toLocalDatetimeString(entry.start),
        amount: entry.amount ? entry.amount.toString() : "",
      });
    } else if (entry.type === "Weight") {
      setEditingValue({
        datetime: toLocalDatetimeString(entry.start),
        weight: entry.weight ? entry.weight.toString() : "",
      });
    } else if (entry.type === "Feeding Plan") {
      setEditingValue({
        datetime: toLocalDatetimeString(entry.scheduled),
        extraMilkMM: entry.extraMilkMM ? entry.extraMilkMM.toString() : "",
        extraMilkMME: entry.extraMilkMME ? entry.extraMilkMME.toString() : "",
        done: entry.done,
      });
    } else if (entry.type === "Medicine") {
      setEditingMedicine(entry);
      setMedicineDialogOpen(true);
      return;
    }
    setEditDialogOpen(true);
  };

  const updateEntryHandler = async () => {
    if (!editingEntry) return;
    try {
      if (editingEntry.type === "Feeding") {
        const newStart = new Date(editingValue.start).toISOString();

        // Handle image upload/removal
        let imageUrl = editingValue.imageUrl || null;
        if (editingImageFile) {
          const compressed = await compressImage(editingImageFile);
          const imageRef = ref(
            storage,
            `children/${childId}/feedingImages/${Date.now()}_${editingImageFile.name}`,
          );
          const snapshot = await uploadBytes(imageRef, compressed);
          imageUrl = await getDownloadURL(snapshot.ref);
          if (editingEntry.imageUrl) {
            try {
              await deleteObject(ref(storage, editingEntry.imageUrl));
            } catch {}
          }
        } else if (!editingImagePreview && editingEntry.imageUrl) {
          try {
            await deleteObject(ref(storage, editingEntry.imageUrl));
          } catch {}
          imageUrl = null;
        }

        const feedingFields = {
          start: newStart,
          side: editingValue.side,
          extraMilkMM: editingValue.extraMilkMM
            ? Number(editingValue.extraMilkMM)
            : 0,
          extraMilkMME: editingValue.extraMilkMME
            ? Number(editingValue.extraMilkMME)
            : 0,
          solidFood: editingValue.solidFood
            ? Number(editingValue.solidFood)
            : 0,
          solidFoodNote: editingValue.solidFoodNote,
          dVitamin: editingValue.dVitamin,
          imageUrl: imageUrl,
        };

        if (editingEntry.active) {
          await updateDoc(
            doc(db, ACTIVE_FEEDING_COL, editingEntry.id),
            feedingFields,
          );
        } else {
          await updateDoc(doc(db, FEEDINGS_COL, editingEntry.id), {
            ...feedingFields,
            end: new Date(editingValue.end).toISOString(),
          });
        }
        setEditingImageFile(null);
        setEditingImagePreview(null);
      } else if (editingEntry.type === "Sleep") {
        const newStart = new Date(editingValue.start).toISOString();
        const headPosition = editingValue.headPosition || "middle";
        if (editingEntry.active) {
          await updateDoc(doc(db, ACTIVE_SLEEP_COL, editingEntry.id), {
            start: newStart,
            headPosition,
          });
        } else {
          await updateDoc(doc(db, SLEEPS_COL, editingEntry.id), {
            start: newStart,
            end: new Date(editingValue.end).toISOString(),
            headPosition,
          });
        }
      } else if (editingEntry.type === "Tummy Time") {
        const start = new Date(editingValue.start).toISOString();
        if (editingEntry.active) {
          await updateDoc(doc(db, ACTIVE_TUMMY_TIME_COL, editingEntry.id), {
            start,
          });
        } else {
          await updateDoc(doc(db, TUMMY_TIMES_COL, editingEntry.id), {
            start,
            end: new Date(editingValue.end).toISOString(),
          });
        }
      } else if (editingEntry.type === "Diaper") {
        const newTimestamp = new Date(editingValue.datetime).toISOString();

        let imageUrl = editingValue.imageUrl || null;
        if (editingImageFile) {
          const compressed = await compressImage(editingImageFile);
          const imageRef = ref(
            storage,
            `children/${childId}/diaperImages/${Date.now()}_${editingImageFile.name}`,
          );
          const snapshot = await uploadBytes(imageRef, compressed);
          imageUrl = await getDownloadURL(snapshot.ref);
          if (editingEntry.imageUrl) {
            try {
              await deleteObject(ref(storage, editingEntry.imageUrl));
            } catch {}
          }
        } else if (!editingImagePreview && editingEntry.imageUrl) {
          try {
            await deleteObject(ref(storage, editingEntry.imageUrl));
          } catch {}
          imageUrl = null;
        }

        await updateDoc(doc(db, DIAPERS_COL, editingEntry.id), {
          start: newTimestamp,
          end: newTimestamp,
          poop: editingValue.poop,
          pee: editingValue.pee,
          color: editingValue.poop === "poop" ? editingValue.color : "",
          konsistens:
            editingValue.poop === "poop"
              ? editingValue.konsistens || "normal"
              : "",
          imageUrl: imageUrl,
        });
        setEditingImageFile(null);
        setEditingImagePreview(null);
      } else if (editingEntry.type === "Extracted Milk") {
        const newTimestamp = new Date(editingValue.datetime).toISOString();
        await updateDoc(doc(db, EXTRACTED_MILK_COL, editingEntry.id), {
          start: newTimestamp,
          end: newTimestamp,
          amount: editingValue.amount ? Number(editingValue.amount) : 0,
        });
      } else if (editingEntry.type === "Weight") {
        const newTimestamp = new Date(editingValue.datetime).toISOString();
        await updateDoc(doc(db, WEIGHTS_COL, editingEntry.id), {
          start: newTimestamp,
          end: newTimestamp,
          weight: editingValue.weight ? Number(editingValue.weight) : 0,
        });
      } else if (editingEntry.type === "Feeding Plan") {
        await updateDoc(doc(db, FEEDING_PLANS_COL, editingEntry.id), {
          scheduled: new Date(editingValue.datetime).toISOString(),
          extraMilkMM: editingValue.extraMilkMM
            ? Number(editingValue.extraMilkMM)
            : 0,
          extraMilkMME: editingValue.extraMilkMME
            ? Number(editingValue.extraMilkMME)
            : 0,
          done: editingValue.done,
        });
      }
      setEditDialogOpen(false);
      setEditingEntry(null);
      setEditingValue({});
      showSnackbar(`${editingEntry.type} entry updated`);
    } catch (error) {
      console.error("Error updating entry: ", error);
    }
  };

  const deleteEntryFromDialog = async () => {
    if (!editingEntry) return;
    const colMap = {
      Feeding: FEEDINGS_COL,
      Sleep: SLEEPS_COL,
      "Tummy Time": TUMMY_TIMES_COL,
      Diaper: DIAPERS_COL,
      "Extracted Milk": EXTRACTED_MILK_COL,
      Weight: WEIGHTS_COL,
      "Feeding Plan": FEEDING_PLANS_COL,
      Medicine: MEDICINES_COL,
    };
    try {
      if (
        editingEntry.imageUrl &&
        (editingEntry.type === "Feeding" || editingEntry.type === "Diaper")
      ) {
        try {
          await deleteObject(ref(storage, editingEntry.imageUrl));
        } catch {}
      }
      await deleteDoc(doc(db, colMap[editingEntry.type], editingEntry.id));
      setEditDialogOpen(false);
      setEditingEntry(null);
      setEditingValue({});
      setEditingImageFile(null);
      setEditingImagePreview(null);
      showSnackbar(`${editingEntry.type} entry deleted`);
    } catch (error) {
      console.error("Error deleting entry: ", error);
    }
  };

  const cancelEdit = () => {
    setEditDialogOpen(false);
    setEditingEntry(null);
    setEditingValue({});
    setEditingImageFile(null);
    setEditingImagePreview(null);
  };

  // ---------------- Render ----------------

  return (
    <Container
      maxWidth="sm"
      sx={{ mt: 2 }}
    >
      <AwakeTimeChip
        lastSleep={lastSleep}
        lastFeeding={lastFeeding}
        activeSleep={activeSleep}
        sleepsData={sleepsData}
        feedingsData={feedingsData}
      />

      <Box sx={{ position: "relative", display: "inline-flex" }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: "bold", fontSize: "2.5rem", color: "#FFF", ml: 1 }}
        >
          {currentChild ? currentChild.name : "Baby"}
        </Typography>
        {currentChild && (
          <Chip
            label={formatChildAge(currentChild.birthDate)}
            size="small"
            onClick={() => setShowLeapInfo(!showLeapInfo)}
            sx={{
              position: "absolute",
              top: 5,
              right: 0,
              transform: "translateX(100%)",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              color: "#FFF",
              fontWeight: "bold",
              cursor: "pointer",
              fontSize: "0.65rem",
              height: 20,
            }}
          />
        )}
      </Box>

      {showLeapInfo && previousLeap && nextLeap && (
        <Typography
          align="center"
          gutterBottom
          variant="body2"
          color="text.disabled"
        >
          <Typography
            component="span"
            sx={{
              cursor: "pointer",
              textDecoration: "underline",
              "&:hover": { textDecoration: "none", opacity: 0.8 },
            }}
            onClick={() => setLeapDialogOpen(true)}
          >
            {previousLeap.title}
          </Typography>{" "}
          ({t("leap.days_ago", { n: daysSinceLastLeap })}). {t("leap.next_in", { n: daysUntilNext })}.
        </Typography>
      )}

      <LeapDialog
        open={leapDialogOpen}
        onClose={() => setLeapDialogOpen(false)}
        leaps={leaps}
        previousLeapId={previousLeap?.id}
      />

      <Tabs
        value={tabIndex}
        onChange={(_, v) => setTabIndex(v)}
        centered
        textColor="inherit"
        indicatorColor="inherit"
        sx={{
          "& .MuiTab-root": { color: "#FFF !important" },
          "& .Mui-selected": { color: "#FFF !important" },
          "& .MuiTabs-indicator": { backgroundColor: "#FFF" },
        }}
      >
        <Tab label={t("tabs.log")} />
        <Tab label={t("tabs.pattern")} />
        <Tab label={t("tabs.weight")} />
        <Tab label={t("tabs.diary")} />
      </Tabs>

      {tabIndex === 0 && (
        <LogTab
          log={log}
          activeFeeding={activeFeeding}
          activeSleep={activeSleep}
          activeTummyTime={activeTummyTime}
          onStopFeeding={stopFeedingHandler}
          onStopSleep={stopSleepHandler}
          onStopTummyTime={stopTummyTimeHandler}
          onStartFeeding={startFeedingHandler}
          onStartSleep={startSleepHandler}
          onStartTummyTime={startTummyTimeHandler}
          onAddDiaper={openDiaperDialog}
          onStartExtractedMilk={startExtractedMilkHandler}
          onEditEntry={openEditDialog}
          onAddMedicine={openAddMedicineDialog}
        />
      )}
      {tabIndex === 1 && (
        <Box sx={{ mt: 2, mb: 8 }}>
          <SleepTimeline
            sleepsData={sleepsData}
            activeSleep={activeSleep}
          />
          <FeedingTimeline
            feedingsData={feedingsData}
            activeFeeding={activeFeeding}
          />
        </Box>
      )}
      {tabIndex === 2 && (
        <WeightTab
          weightLog={weightLog}
          onAddWeight={addWeightHandler}
          onEditEntry={openEditDialog}
          currentChild={currentChild}
          getChildAgeAtEntry={getChildAgeAtEntry}
        />
      )}
      {tabIndex === 3 && (
        <Box sx={{ mt: 2 }}>
          <DiaryList />
        </Box>
      )}

      <Dialog
        open={diaperDialogOpen}
        onClose={() => setDiaperDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t("diaper.register_title")}</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
        >
          <Box>
            <Typography
              variant="body2"
              sx={{ mb: 0.5 }}
            >
              {t("diaper.poop")}
            </Typography>
            <ToggleButtonGroup
              value={diaperForm.poop}
              exclusive
              onChange={(_, v) =>
                v &&
                setDiaperForm((f) => ({ ...f, poop: v, konsistens: "normal" }))
              }
              fullWidth
              size="small"
            >
              <ToggleButton value="none">{t("common.no")}</ToggleButton>
              <ToggleButton value="poop">{t("common.yes")}</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {diaperForm.poop === "poop" && (
            <>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ mb: 0.5 }}
                >
                  {t("diaper.consistency")}
                </Typography>
                <ToggleButtonGroup
                  value={diaperForm.konsistens}
                  exclusive
                  onChange={(_, v) =>
                    v && setDiaperForm((f) => ({ ...f, konsistens: v }))
                  }
                  fullWidth
                  size="small"
                >
                  {CONSISTENCY_OPTIONS.map((opt) => (
                    <ToggleButton
                      key={opt.value}
                      value={opt.value}
                    >
                      {opt.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ mb: 0.5 }}
                >
                  {t("diaper.color")}
                </Typography>
                <ToggleButtonGroup
                  value={diaperForm.color}
                  exclusive
                  onChange={(_, v) =>
                    v && setDiaperForm((f) => ({ ...f, color: v }))
                  }
                  fullWidth
                  size="small"
                  sx={{ overflow: "visible" }}
                >
                  {[
                    { value: "#FFF59D", label: t("diaper.yellow") },
                    { value: "#8D6E63", label: t("diaper.brown") },
                    { value: "#AED581", label: t("diaper.green") },
                    { value: "#C1DABF", label: t("diaper.yellow_green") },
                  ].map((c) => (
                    <ToggleButton
                      key={c.value}
                      value={c.value}
                      sx={{
                        backgroundColor: c.value,
                        "&&.Mui-selected": {
                          backgroundColor: c.value,
                          boxShadow: "inset 0 0 0 3px #8fb9a8",
                        },
                        "&&.Mui-selected:hover": {
                          backgroundColor: c.value,
                        },
                      }}
                    >
                      {c.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            </>
          )}
          <Box>
            <Typography
              variant="body2"
              sx={{ mb: 0.5 }}
            >
              {t("diaper.pee")}
            </Typography>
            <ToggleButtonGroup
              value={diaperForm.pee}
              exclusive
              onChange={(_, v) => v && setDiaperForm((f) => ({ ...f, pee: v }))}
              fullWidth
              size="small"
            >
              <ToggleButton value="none">{t("common.no")}</ToggleButton>
              <ToggleButton value="pee">{t("common.yes")}</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiaperDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            onClick={saveDiaperEntry}
          >
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <MedicineDialog
        open={medicineDialogOpen}
        onClose={() => {
          setMedicineDialogOpen(false);
          setEditingMedicine(null);
        }}
        onSave={handleSaveMedicine}
        onDelete={editingMedicine ? handleDeleteMedicine : undefined}
        initialStart={editingMedicine?.start}
        initialMedicines={editingMedicine?.medicines}
        initialFever={editingMedicine?.fever}
      />

      <EditEntryDialog
        open={editDialogOpen}
        editingEntry={editingEntry}
        editingValue={editingValue}
        setEditingValue={setEditingValue}
        onSave={updateEntryHandler}
        onDelete={deleteEntryFromDialog}
        onCancel={cancelEdit}
        editingImageFile={editingImageFile}
        setEditingImageFile={setEditingImageFile}
        editingImagePreview={editingImagePreview}
        setEditingImagePreview={setEditingImagePreview}
      />

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      >
        <SnackbarContent
          message={snackbarMessage}
          sx={{ backgroundColor: snackbarBgColor, color: "#000" }}
        />
      </Snackbar>
    </Container>
  );
};

export default BabyTracker;
